"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.updateRoutingEndpointInput = exports.createRoutingEndpointInput = exports.updateRoutingContactInput = exports.createRoutingContactInput = exports.routingDirectorySchema = exports.routingContactSchema = exports.routingEndpointSchema = void 0;
/**
 * `RPM-105` — a building's routing directory: its contacts and their endpoints (`J-487`).
 *
 * A CONTACT IS A LABELLED RECIPIENT — a person or a role, the label kept as written and possibly blank —
 * scoped to the whole building or to one care unit. Its ENDPOINTS are an email address (flagged
 * summary-only where it receives only summaries) or a phone value with its kind, whether it may be
 * called, its extension, and when it is reachable. Values travel in bodies only, never in a path or a
 * query string, and are configuration, not PHI (`03-RPM-SCHEMA.md` §4.2.12).
 *
 * THE ENDPOINT'S SHAPE IS THE DATABASE'S TO REFUSE (the block's Blocking row): an email with a phone
 * attribute, a phone with no kind, a summary-only phone. The body schemas here therefore accept those
 * fields loosely, and the four `rre_*` checks answer; the service turns their names into a `422`.
 */
const zod_1 = require("zod");
const rpm_enums_js_1 = require("./db/rpm-enums.js");
const HOUR = zod_1.z.number().int().min(0).max(24);
/** A deactivation that removes a building's last callable phone or last alert email says why. */
const STATED_REASON = zod_1.z.string().trim().min(1).max(500);
exports.routingEndpointSchema = zod_1.z.object({
    routingEndpointId: zod_1.z.uuid(),
    channel: zod_1.z.enum(rpm_enums_js_1.endpointChannel.enumValues),
    value: zod_1.z.string(),
    phoneKind: zod_1.z.enum(rpm_enums_js_1.phoneKind.enumValues).nullable(),
    isCallable: zod_1.z.boolean().nullable(),
    isSummaryOnly: zod_1.z.boolean(),
    extension: zod_1.z.string().nullable(),
    reachWindow: zod_1.z.enum(rpm_enums_js_1.reachWindow.enumValues),
    reachDays: zod_1.z.enum(rpm_enums_js_1.reachDays.enumValues).nullable(),
    reachStartHour: HOUR.nullable(),
    reachEndHour: HOUR.nullable(),
    isActive: zod_1.z.boolean(),
});
exports.routingContactSchema = zod_1.z.object({
    routingContactId: zod_1.z.uuid(),
    facilityId: zod_1.z.string().min(1).max(20),
    scope: zod_1.z.enum(rpm_enums_js_1.routingContactScope.enumValues),
    careUnitId: zod_1.z.uuid().nullable(),
    /** The care unit's label, for a unit-scoped contact; null for a building-wide one. */
    careUnitLabel: zod_1.z.string().nullable(),
    label: zod_1.z.string().nullable(),
    isActive: zod_1.z.boolean(),
    endpoints: zod_1.z.array(exports.routingEndpointSchema),
});
exports.routingDirectorySchema = zod_1.z.object({
    facilityId: zod_1.z.string().min(1).max(20),
    contacts: zod_1.z.array(exports.routingContactSchema),
});
exports.createRoutingContactInput = zod_1.z.strictObject({
    scope: zod_1.z.enum(rpm_enums_js_1.routingContactScope.enumValues),
    careUnitId: zod_1.z.uuid().nullable().optional(),
    label: zod_1.z.string().max(200).nullable().optional(),
});
exports.updateRoutingContactInput = zod_1.z
    .strictObject({
    label: zod_1.z.string().max(200).nullable().optional(),
    isActive: zod_1.z.literal(false).optional(),
    statedReason: STATED_REASON.optional(),
})
    .refine((v) => v.label !== undefined || v.isActive !== undefined, {
    message: "Send a label or a deactivation.",
});
/** Every attribute an endpoint can carry. Which ones a channel admits is the database's rule. */
const endpointFields = {
    value: zod_1.z.string().trim().min(1).max(320),
    phoneKind: zod_1.z.enum(rpm_enums_js_1.phoneKind.enumValues).nullable().optional(),
    isCallable: zod_1.z.boolean().nullable().optional(),
    isSummaryOnly: zod_1.z.boolean().optional(),
    extension: zod_1.z.string().trim().max(20).nullable().optional(),
    reachWindow: zod_1.z.enum(rpm_enums_js_1.reachWindow.enumValues).optional(),
    reachDays: zod_1.z.enum(rpm_enums_js_1.reachDays.enumValues).nullable().optional(),
    reachStartHour: HOUR.nullable().optional(),
    reachEndHour: HOUR.nullable().optional(),
};
exports.createRoutingEndpointInput = zod_1.z.strictObject({
    channel: zod_1.z.enum(rpm_enums_js_1.endpointChannel.enumValues),
    ...endpointFields,
});
exports.updateRoutingEndpointInput = zod_1.z
    .strictObject({
    ...endpointFields,
    value: endpointFields.value.optional(),
    isActive: zod_1.z.literal(false).optional(),
    statedReason: STATED_REASON.optional(),
})
    .refine((v) => Object.keys(v).some((k) => k !== "statedReason"), {
    message: "Send at least one field to change.",
});
//# sourceMappingURL=rpm-routing-directory.js.map